import { RefundDestinationType } from '../enums';
import { RefundCapabilities } from './Order';

export interface Refund {
  id?: string;
  gift_card?: string;
  order_id?: string;
  reason?: string;
  total_amount?: number;
  total_extra_costs?: number;
  total_refunded?: number;
  refund_status?: string;
  refunded_order_lines?: { order_line_id: string }[];
  extra_costs?: { product_id: string }[];
  // Refund capability of the order, NOT an amount: it was typed as `number` and PAUL-2852 read it as
  // the net refundable, so Full = 0 opened the allocation builder at € 0,00.
  refundable?: RefundCapabilities;
  error_message?: string;
  // PAUL-2852: preferred destinations the operator may route (part of) the refund to,
  // computed for the caller's channel/POS. Replaces supported_refund_modes.
  available_destinations?: RefundDestination[];
  // PAUL-2852: operator-chosen allocations sent at finalize. Mutually exclusive with refund_mode.
  allocations?: RefundAllocation[];
  /** @deprecated PAUL-2852 (until 2026-10-22) — use available_destinations. */
  supported_refund_modes?: RefundMode[];
  /** @deprecated PAUL-2852 (until 2026-10-22) — use allocations[]. */
  refund_mode?: RefundMode;
  email?: string;
}

// PAUL-2852: one item of Refund.available_destinations (response).
export interface RefundDestination {
  payment_method_id: string;
  integration_id: string;
  destination_type: RefundDestinationType;
  label: string;
  // null = uncapped (Yes-always); otherwise the max routable to this destination.
  cap: number | null;
  // Psp / GiftCardReload — the order payment routed back to / reloaded from.
  originating_order_payment_id?: string | null;
  // GiftCardReload — the existing gift card.
  originating_gift_card_id?: string | null;
  // GiftCardReload display — gift card code masked to its last 4 chars (e.g. "••••4242").
  gift_card_masked?: string | null;
  // Diagnostic; a destination without a G/L account is not offered.
  requires_general_ledger_account: boolean;
}

// PAUL-2852: one item of FinalizeRefundModel.allocations (request). Amounts must sum to net refundable.
export interface RefundAllocation {
  payment_method_id: string;
  integration_id: string;
  destination_type: RefundDestinationType;
  amount: number;
  // Psp / GiftCardReload.
  originating_order_payment_id?: string | null;
  // GiftCardReload.
  originating_gift_card_id?: string | null;
  // GiftCardNew (email variant) recipient.
  email?: string;
  // GiftCardNew (physical) optional code override.
  code?: string;
  // GiftCardNew — PhysicalGiftcard / EmailGiftcard / GiftcardAsReceipt.
  gift_card_variant?: RefundMode;
  // GiftCardNew (email variant) — the account the autocomplete pick stored (PAUL-3595). Wins over the e-mail match.
  user_id?: string;
}

export interface BulkRefundResponse {
  orders: Refund[];
  refund_mode: RefundMode;
}

export enum RefundMode {
  PhysicalGiftcard = 0,
  EmailGiftcard = 1,
  BackToOriginalPayment = 2,
  ReleaseTickets = 3,
  // PAUL-2852: gift card printed as a receipt (physical POS variant of GiftCardNew).
  GiftcardAsReceipt = 4,
}
